import React, { useState, useEffect, useRef, useCallback } from "react";
import { Alert, Button, IconButton, Stack, Typography } from "@mui/material";
import {
  query,
  orderBy,
  limit,
  onSnapshot,
  startAfter,
  getDocs,
  collection,
  where,
} from "firebase/firestore";
import { useTheme } from "@emotion/react";
import { db } from "firebaseApp";
import { COLLECTIONS } from "firebaseCollections";
import MessageV2 from "components/chat-v2/MessageV2";
import ReplayIcon from "@mui/icons-material/Replay";
import { replayMessage } from "components/chat-v2/chat";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

const ConversationMessages = ({
  uid,
  contextSize,
  initialSlice = 10,
  loadSlice = 10,
}) => {
  const params = useParams();
  const conversationId = params.conversationId;

  const messageQuery = conversationId
    ? query(
        collection(db, COLLECTIONS.messages),
        where("uid", "==", uid),
        where("conversationId", "==", conversationId),
        where("cancelled", "==", false)
      )
    : null;

  const theme = useTheme();

  const autoScroll = useRef(true);

  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const messagesListRef = useRef(null);
  const lastVisible = useRef(null);
  const isLoading = useRef(false);
  const previousLastElemRef = useRef(null);

  // const localStream = useSelector((state) => state.chat.localStream);

  useEffect(() => {
    setMessages([]);
    setHasMore(true);
    lastVisible.current = null;
    isLoading.current = false;
    previousLastElemRef.current = null;
    const q = query(
      messageQuery,
      orderBy("createdAt", "desc"),
      limit(initialSlice)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      if (messages.length > 0) {
        setMessages(messages.reverse());
        lastVisible.current = snapshot.docs[snapshot.docs.length - 1];
      } else {
        setHasMore(false);
        setMessages([]);
        lastVisible.current = null;
        previousLastElemRef.current = null;
      }
    });
    return () => unsubscribe();
  }, [conversationId]);

  const loadMore = async () => {
    if (isLoading.current || !hasMore) return;
    isLoading.current = true;
    const q = query(
      messageQuery,
      orderBy("createdAt", "desc"),
      startAfter(lastVisible.current),
      limit(loadSlice)
    );
    const nextElemsSnapshot = await getDocs(q);
    const nextElems = nextElemsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    if (nextElems.length > 0) {
      setMessages((prevElems) => [...nextElems.reverse(), ...prevElems]);
      lastVisible.current =
        nextElemsSnapshot.docs[nextElemsSnapshot.docs.length - 1];
    } else {
      setHasMore(false);
    }
    isLoading.current = false;
  };

  const handleScroll = () => {
    const list = messagesListRef.current;
    if (!list) return;
    const atBottom =
      list.scrollHeight - list.scrollTop <= list.clientHeight + 5;
    autoScroll.current = atBottom;
  };

  const scrollToBottom = useCallback(() => {
    const list = messagesListRef.current;
    list.scrollTop = list.scrollHeight;
  }, []);

  useEffect(() => {
    // autoScroll.current = true;
    if (messages.length > 0) {
      const lastElem = messages[messages.length - 1];
      const prevLastElem = previousLastElemRef.current;
      if (lastElem && (!prevLastElem || lastElem.id !== prevLastElem.id)) {
        if (autoScroll.current) {
          scrollToBottom();
        }
        previousLastElemRef.current = lastElem;
      }
    }
  }, [conversationId, messages, scrollToBottom]);

  const handleData = (data) => {
    switch (data.status) {
      case "created":
        autoScroll.current = true;
        scrollToBottom();
        break;
      case "streaming":
        if (autoScroll.current) {
          scrollToBottom();
        }
        break;
    }
  };

  const renderRetry = () => {
    if (!messages || messages.length === 0) return null;
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage.hasError) return null;
    return (
      <Stack
        pt={2}
        width="100%"
        sx={{
          maxWidth: "748px",
        }}
      >
        <Alert
          variant="outlined"
          severity="error"
          action={
            <IconButton onClick={() => replayMessage(lastMessage)}>
              <ReplayIcon />
            </IconButton>
          }
        >
          {lastMessage.error || "An error occurred."}
        </Alert>
      </Stack>
    );
  };

  return (
    <Stack
      spacing={1}
      sx={{
        width: "100%",
        alignItems: "center",
        overflowY: "auto",
        overflowX: "none",
      }}
      ref={messagesListRef}
      onWheel={handleScroll}
    >
      {!hasMore && (
        <Typography sx={{ color: theme.palette.text.main }} textAlign="center">
          This is the start of this conversation.
        </Typography>
      )}
      {hasMore && (
        <Button onClick={loadMore} disabled={isLoading.current} color="text">
          Load More
        </Button>
      )}
      {messages.map((message, index) => (
        <MessageV2
          key={message.id}
          message={message}
          inContext={messages.length - index <= 2 * contextSize}
          onData={handleData}
        />
        // <Typography
        //   key={message.id}
        //   sx={{
        //     color: theme.palette.text.main,
        //   }}
        // >
        //   {message.content}
        // </Typography>
      ))}
      {renderRetry()}
    </Stack>
  );
};

export default ConversationMessages;
